import { useEffect, useRef } from 'react';
import {
  fetchJobStatus,
  JobNotFoundError,
  JobPollEventPayload,
  JobPollResponse,
} from '../api/jobs';

/** A refresh can poll before science has registered the job id. */
const MAX_NOT_FOUND_ATTEMPTS = 5;

const DEFAULT_POLL_INTERVAL_MS = 3000;

interface UseJobPollingParams {
  /** Poll while set; polling stops (and resets) when this becomes null. */
  jobId: string | null;
  intervalMs?: number;
  /** Called once per not-yet-seen event, in order, every poll tick. */
  onEvent: (event: JobPollEventPayload) => void;
  /** Terminal: status === 'done'. */
  onDone: (response: JobPollResponse) => void;
  /** Terminal: status === 'error', the job expired (404), or the request itself failed. */
  onError: (response: JobPollResponse | null, error?: unknown) => void;
  /** Terminal: status === 'cancelled'. Stops polling without treating it as a failure. */
  onCancelled?: (response: JobPollResponse) => void;
}

/**
 * Polls GET /api/v1/jobs/{job_id} on an interval, replaying only the events
 * not yet delivered (the endpoint returns the full ordered list every time).
 * Also polls immediately when the tab regains visibility — a background
 * tab's timers get throttled, so without this a finished job could sit
 * unnoticed until the next scheduled tick fires.
 */
export const useJobPolling = ({
  jobId,
  intervalMs = DEFAULT_POLL_INTERVAL_MS,
  onEvent,
  onDone,
  onError,
  onCancelled,
}: UseJobPollingParams): void => {
  const callbacksRef = useRef({ onEvent, onDone, onError, onCancelled });
  callbacksRef.current = { onEvent, onDone, onError, onCancelled };

  useEffect(() => {
    if (!jobId) {
      return;
    }

    let cancelled = false;
    let timeoutId: number | undefined;
    let consumedCount = 0;
    let inFlight = false;
    let notFoundAttempts = 0;

    const poll = async () => {
      if (cancelled || inFlight) {
        return;
      }
      inFlight = true;
      try {
        const response = await fetchJobStatus(jobId);
        if (cancelled) {
          return;
        }

        const newEvents = response.events.slice(consumedCount);
        consumedCount = response.events.length;
        newEvents.forEach((event) => callbacksRef.current.onEvent(event));

        if (response.status === 'done') {
          callbacksRef.current.onDone(response);
          return;
        }
        if (response.status === 'cancelled') {
          if (callbacksRef.current.onCancelled) {
            callbacksRef.current.onCancelled(response);
          } else {
            callbacksRef.current.onError(response);
          }
          return;
        }
        if (response.status === 'error') {
          callbacksRef.current.onError(response);
          return;
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (error instanceof JobNotFoundError && notFoundAttempts < MAX_NOT_FOUND_ATTEMPTS) {
          notFoundAttempts += 1;
        } else {
          // A repeated 404, or any other request failure, ends polling. The
          // caller decides how to surface it.
          callbacksRef.current.onError(null, error);
          return;
        }
      } finally {
        inFlight = false;
      }

      if (!cancelled) {
        timeoutId = window.setTimeout(poll, intervalMs);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !cancelled) {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
        void poll();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    void poll();

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [jobId, intervalMs]);
};
